import { useState, useEffect } from 'react';
import { fetchGitHubLanguages, isOpenSourceGitHubRepo } from '@/lib/skills';
import { activities } from '../../../db';
import { sortSkillItemsByWeight } from '@/lib/skills';

interface SkillItem {
  name: string;
  weight: number;
}

interface EnhancedSkillsByCategory {
  profession: SkillItem[];
  lead: SkillItem[];
  language: SkillItem[];
  tools: SkillItem[];
}

interface UseActivitySpecificProjectSkillsProps {
  project: {
    git?: string;
    startDate: string;
    endDate: string | null;
    activityId: number | number[] | null;
    categorizedSkills?: {
      profession: SkillItem[];
      lead: SkillItem[];
      language: SkillItem[];
      tools: SkillItem[];
    };
    skills?: SkillItem[];
  };
  activityId?: number; // Optional activity context
}

/**
 * Calculate duration in months between two dates
 */
function calculateDurationInMonths(startDate: string, endDate: string | null): number {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  
  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();
  
  return yearDiff * 12 + monthDiff + 1; // +1 to include the start month
}

/**
 * Calculate the effective start and end dates for a project within a specific activity
 */
function getActivityProjectPeriod(
  project: { startDate: string; endDate: string | null; activityId: number | number[] | null },
  activityId: number
): { startDate: string; endDate: string | null } {
  // If no activity context or project is not associated with this activity, return full project period
  if (!project.activityId || project.activityId === null) {
    return { startDate: project.startDate, endDate: project.endDate };
  }

  const projectActivityIds = Array.isArray(project.activityId) 
    ? project.activityId 
    : [project.activityId];

  // If project is not associated with this activity, return full project period
  if (!projectActivityIds.includes(activityId)) {
    return { startDate: project.startDate, endDate: project.endDate };
  }

  // Get the activity dates
  const activity = activities.find(a => a.id === activityId);
  if (!activity) {
    return { startDate: project.startDate, endDate: project.endDate };
  }

  // For single-activity projects, return full project period
  if (!Array.isArray(project.activityId)) {
    return { startDate: project.startDate, endDate: project.endDate };
  }

  // For multi-activity projects, calculate the intersection of project and activity periods
  const projectStart = new Date(project.startDate);
  const projectEnd = project.endDate ? new Date(project.endDate) : null;
  const activityStart = new Date(activity.startDate);
  const activityEnd = activity.endDate ? new Date(activity.endDate) : null;

  // Calculate effective start date (later of project start or activity start)
  const effectiveStart = projectStart > activityStart ? projectStart : activityStart;

  // Calculate effective end date (earlier of project end or activity end, handling nulls)
  let effectiveEnd: Date | null = null;
  if (projectEnd === null && activityEnd === null) {
    // Both are ongoing - no end date
    effectiveEnd = null;
  } else if (projectEnd === null) {
    // Project is ongoing, activity has end date
    effectiveEnd = activityEnd;
  } else if (activityEnd === null) {
    // Activity is ongoing, project has end date
    effectiveEnd = projectEnd;
  } else {
    // Both have end dates - use earlier one
    effectiveEnd = projectEnd < activityEnd ? projectEnd : activityEnd;
  }

  return {
    startDate: effectiveStart.toISOString().split('T')[0],
    endDate: effectiveEnd ? effectiveEnd.toISOString().split('T')[0] : null
  };
}

/**
 * Custom hook that enhances project skills with GitHub-fetched languages
 * and optionally calculates skills for a specific activity time period
 */
export function useActivitySpecificProjectSkills({ project, activityId }: UseActivitySpecificProjectSkillsProps) {
  const [enhancedSkills, setEnhancedSkills] = useState<EnhancedSkillsByCategory | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [languagesFromGitHub, setLanguagesFromGitHub] = useState<{ fromGitHub: boolean; githubUrl?: string }>({ fromGitHub: false });
  const [effectivePeriod, setEffectivePeriod] = useState<{ startDate: string; endDate: string | null } | null>(null);

  useEffect(() => {
    const enhanceSkillsWithGitHub = async () => {
      // Calculate the effective time period for this project within the activity context
      let period = { startDate: project.startDate, endDate: project.endDate };
      if (activityId) {
        period = getActivityProjectPeriod(project, activityId);
      }
      setEffectivePeriod(period);

      // Start with the project's existing skills or convert from legacy format
      let baseSkills: EnhancedSkillsByCategory;
      let gitHubLanguageInfo = { fromGitHub: false, githubUrl: undefined as string | undefined };
      
      if (project.categorizedSkills) {
        // Use new categorized format
        baseSkills = { ...project.categorizedSkills };
      } else {
        // Convert from legacy format
        baseSkills = {
          profession: project.skills || [],
          lead: [],
          language: [],
          tools: [],
        };
      }

      // Check if this is an open source GitHub project
      if (isOpenSourceGitHubRepo(project.git)) {
        setIsLoading(true);
        try {
          // Fetch languages from GitHub
          const githubLanguages = await fetchGitHubLanguages(project.git!);
          
          if (githubLanguages.length > 0) {
            // Replace the language skills with GitHub-fetched data
            baseSkills.language = githubLanguages.map(lang => ({
              name: lang.name,
              weight: lang.weight
            }));
            
            // Mark that languages came from GitHub
            gitHubLanguageInfo = { 
              fromGitHub: true, 
              githubUrl: project.git!
            };
          }
        } catch (error) {
          console.warn('Failed to enhance project skills with GitHub data:', error);
        } finally {
          setIsLoading(false);
        }
      }

      setEnhancedSkills(sortSkillItemsByWeight(baseSkills));
      setLanguagesFromGitHub(gitHubLanguageInfo);
    };

    enhanceSkillsWithGitHub();
  }, [project.git, project.categorizedSkills, project.skills, project.startDate, project.endDate, project.activityId, activityId]);

  return {
    skillsByCategory: enhancedSkills,
    isLoading,
    languagesFromGitHub,
    effectivePeriod
  };
}
